// ============================================================================
// RHSolicitacoes.js
// DEMANDAS PARA RH dentro do Módulo de RH — o balcão de documentos da loja.
// Padrão visual: RH / Vermelho. Mesmo molde do RHForms.js (grade de cards +
// views embutidas), com dois verbos no card: Modelo / Enviar.
//
// ⚠️ O CARD NÃO TEM MAIS "Consultar" (saiu em 15/09/2026): ele abria a MESMA
//    tela da aba "Todas as demandas", só que com o filtro de tipo preenchido —
//    e esse filtro está lá, no seletor. Duas portas para o mesmo lugar é o tipo
//    de duplicação que envelhece mal: uma delas para de receber ajuste.
//
// ⚠️⚠️ A GRADE NÃO É ESCRITA AQUI — ela vem de GET /api/rh/solicitacoes/tipos.
//    O catálogo mora no backend (server/rh/solicitacoes-tipos.js) de propósito:
//    uma cópia no front erraria EM SILÊNCIO (o card sumiria do sub-hub, ou o
//    servidor recusaria um campo que a tela mandou, sem nada apontar a causa).
//    Tipo novo aparece aqui sozinho, sem tocar neste arquivo.
//
// ⚠️ UM CARD SE COMPORTA DIFERENTE DOS OUTROS: o Término do Contrato de Experiência (chave `cancelamento-contrato`) é
//    `formato: 'tela'` — não é papel. Ele tem módulo próprio (já no ar, com a
//    lista de candidatos calculada ao vivo do espelho de ponto) e por isso abre
//    a tela dele, sem Modelo nem Enviar. A descrição do card precisa deixar isso
//    óbvio, senão o gerente procura onde anexar e não acha.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, BusFront, CheckCircle2, Clock, CreditCard, Download,
  FileSignature, FileUp, Inbox, LogOut, Settings, Timer, UserPen, UserX,
} from 'lucide-react';

import RHSolicitacoesEnviar from './RHSolicitacoes_Enviar';
import RHSolicitacoesFila from './RHSolicitacoes_Fila';
import RHSolicitacoesModelos from './RHSolicitacoes_Modelos';
import RHCancelamentoAdmissao from '../cancelamento-admissao/RHCancelamentoAdmissao';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { baixarArquivoComToken } from '../../../utils/arquivoComToken';

const COR = '#e11d48';
const COR_ESCURA = '#be123c';
const COR_FUNDO = '#fff1f2';

// O catálogo manda o NOME do ícone (string) — o backend não importa React.
// Nome desconhecido cai no genérico, nunca quebra a tela.
const ICONES = {
  UserX, UserPen, LogOut, AlertTriangle, FileSignature, BusFront, CreditCard, Clock, Timer,
};

function RHSolicitacoes() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = perfilNoModulo(user?.perfil, 'rh') || '';

  const isAdmin = role === 'Admin';
  const isRH = role === 'RH';
  const isDiretoria = role === 'Diretoria';
  const ehLoja = ['Gerente', 'Gerente Adm', 'Gerente Plantao', 'Operacional', 'Coordenador de Qualidade'].includes(role);

  // Espelha o backend: PERFIL_ABRE ∪ PERFIL_TRATA enviam; PERFIL_TRATA trata;
  // PERFIL_ADMIN_RH publica modelo.
  const podeEnviar = ehLoja || isRH || isDiretoria || isAdmin;
  const podeTratar = isRH || isDiretoria || isAdmin;
  const podeModelos = isRH || isAdmin;

  const [tipos, setTipos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  // vista: { nome: 'visao-geral' | 'enviar' | 'fila' | 'modelos' | 'cancelamento', tipo? }
  const [vista, setVista] = useState({ nome: 'visao-geral' });
  const [baixando, setBaixando] = useState(null);

  const carregarTipos = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/solicitacoes/tipos', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Não foi possível carregar as demandas.');
      // ⚠️ A ORDEM VEM DO CATÁLOGO, na ordem em que o backend declara os tipos —
      // e a tela NÃO reordena. Ela era alfabética até 15/09/2026; o RH definiu
      // uma ordem de negócio (contrato e benefício primeiro, ocorrência depois)
      // e ela mora em server/rh/solicitacoes-tipos.js, junto do resto do tipo.
      setTipos(await resp.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarTipos(); }, [carregarTipos]);

  const baixarModelo = async (tipo) => {
    setBaixando(tipo.chave);
    setErro(null);
    try {
      await baixarArquivoComToken(
        `/api/rh/solicitacoes/modelos/${tipo.chave}/arquivo`,
        `${tipo.rotulo}.pdf`
      );
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(null);
    }
  };

  // -------------------------------------------------------------------
  // Card de um tipo. Os botões saem do `formato` — não de uma lista escrita
  // à mão aqui, que voltaria a ser o espelho que o catálogo veio eliminar.
  // -------------------------------------------------------------------
  const Card = ({ tipo }) => {
    const Icone = ICONES[tipo.icone] || Inbox;
    const ehTela = tipo.formato === 'tela';
    const ehMensal = tipo.periodicidade === 'MENSAL';

    return (
      <div
        style={{
          backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
          border: '1px solid #f0e2e2', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
          display: 'flex', flexDirection: 'column', height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)'; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div style={{ backgroundColor: `${COR}15`, color: COR, padding: '16px', borderRadius: '12px', display: 'flex' }}>
            <Icone size={28} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: 0 }}>{tipo.rotulo}</h3>
            {/* ⚠️ O selo MÊS é funcional, não decorativo: sem ele o gerente manda
                um documento por ocorrência, e o mensal é UM papel com todas as
                datas e uma assinatura. */}
            {ehMensal && (
              <span style={{
                display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 999,
                background: '#fef3c7', color: '#b45309', fontSize: 11, fontWeight: 800,
                letterSpacing: '.3px',
              }}>
                UM POR MÊS
              </span>
            )}
          </div>
        </div>

        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.5, flex: 1, marginBottom: '24px' }}>
          {tipo.descricao}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {ehTela ? (
            <button
              onClick={() => setVista({ nome: 'cancelamento' })}
              style={{ ...botaoBase, backgroundColor: COR, color: '#fff', border: 'none' }}
            >
              <FileUp size={16} /> Abrir
            </button>
          ) : (
            <>
              {/* "Baixar modelo" só aparece quando há modelo vigente publicado —
                  na subida os 9 nascem sem modelo. */}
              {tipo.temModelo && (
                <button
                  onClick={() => baixarModelo(tipo)}
                  disabled={baixando === tipo.chave}
                  style={{ ...botaoBase, backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                >
                  <Download size={16} /> {baixando === tipo.chave ? 'Baixando…' : 'Modelo'}
                </button>
              )}
              {podeEnviar && (
                <button
                  onClick={() => setVista({ nome: 'enviar', tipo })}
                  style={{ ...botaoBase, backgroundColor: COR, color: '#fff', border: 'none' }}
                >
                  <FileUp size={16} /> {ehMensal ? 'Enviar mês' : 'Enviar'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------
  // Views internas (o "Voltar" devolve à grade, como no RHForms).
  // -------------------------------------------------------------------
  if (vista.nome !== 'visao-geral') {
    const Conteudo = () => {
      if (vista.nome === 'cancelamento') return <RHCancelamentoAdmissao />;
      if (vista.nome === 'modelos') return <RHSolicitacoesModelos tipos={tipos} aoPublicar={carregarTipos} />;
      if (vista.nome === 'enviar') {
        return (
          <RHSolicitacoesEnviar
            tipo={vista.tipo}
            aoEnviar={() => setVista({ nome: 'fila', tipo: vista.tipo })}
          />
        );
      }
      return <RHSolicitacoesFila tipoInicial={vista.tipo ? vista.tipo.chave : ''} tipos={tipos} podeTratar={podeTratar} />;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <button
          onClick={() => setVista({ nome: 'visao-geral' })}
          style={{
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', borderRadius: '8px', border: `1px solid ${COR_FUNDO}`,
            backgroundColor: COR_FUNDO, color: COR_ESCURA, fontSize: '14px', fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} /> Voltar às demandas
        </button>
        <Conteudo />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center',
        borderBottom: '1px solid #f0e2e2', paddingBottom: '16px',
      }}>
        <button style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
          backgroundColor: COR_FUNDO, color: COR, border: `1px solid ${COR}`,
          borderRadius: '8px', fontWeight: 700, fontSize: '14px', cursor: 'default',
        }}>
          <CheckCircle2 size={18} /> Visão Geral
        </button>

        <button
          onClick={() => setVista({ nome: 'fila' })}
          style={{ ...botaoBase, flex: 'none', backgroundColor: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px 18px' }}
        >
          <Inbox size={18} /> Todas as demandas
        </button>

        {podeModelos && (
          <button
            onClick={() => setVista({ nome: 'modelos' })}
            style={{ ...botaoBase, flex: 'none', backgroundColor: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px 18px' }}
          >
            <Settings size={18} /> Modelos
          </button>
        )}
      </div>

      {erro && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 14 }}>
          {erro}
        </div>
      )}

      {carregando ? (
        <p style={{ color: '#64748b', fontSize: 14 }}>Carregando demandas…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
          {tipos.map((t) => <Card key={t.chave} tipo={t} />)}
        </div>
      )}
    </div>
  );
}

// Base dos botões do card (mesma métrica do RHForms).
const botaoBase = {
  flex: 1, minWidth: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: '8px', padding: '10px', borderRadius: '8px', fontWeight: 600, fontSize: '13px',
  cursor: 'pointer', transition: 'opacity 0.2s',
};

export default RHSolicitacoes;
